import { useTheme } from 'canoe';
import React, { useMemo } from 'react';
import { Line, LineChart, ResponsiveContainer, Tooltip } from 'recharts';

import { AnalyticQuery, TimePeriod } from '@candu-ai/analytics/types';
import { CustomTooltip, getMomentPeriodFormat, getSeriesColor, GraphEmbed } from '@candu-ai/analytics/ui';
import { CONTENT_SYNC_ID } from 'dashboard/pages/Content/pages/Analytics/types';

interface ChartProps {
  unit?: '%';
  legend: string;
}

const Chart = ({
  data,
  unit,
  legend,
}: ChartProps & {
  data: AnalyticQuery<any>;
}) => {
  const theme = useTheme();

  const [xAxis, yAxis] = data.fields;

  const chartData = useMemo(() => {
    if (!data.metadata) {
      return [];
    }

    const dataTimestampLookup = Object.fromEntries(
      data.rows.map((row) => [row[xAxis.name], row[yAxis.name]]),
    );

    // create a date xAxis from time of the first entry
    const timestamps = data.metadata.time.timestamps || [];

    return timestamps?.map((timestamp) => ({
      [xAxis.name]: timestamp,
      [yAxis.name]: dataTimestampLookup[timestamp] ?? 0,
    }));
  }, [data, xAxis, yAxis]);

  return (
    <div style={{ fontSize: '12px' }}>
      <GraphEmbed executedAt={data.executedAt} height={100}>
        <ResponsiveContainer width="100%" height={100}>
          <LineChart data={chartData} syncId={CONTENT_SYNC_ID}>
            <Tooltip
              content={CustomTooltip(
                legend,
                false,
                getMomentPeriodFormat(data.metadata.time.period || TimePeriod.Days) as any,
                unit,
              )}
            />

            <Line
              key={yAxis.name}
              type="monotone"
              name={yAxis.name}
              dataKey={yAxis.name}
              stroke={theme.colors[getSeriesColor(0)]}
              dot={() => null}
            />
          </LineChart>
        </ResponsiveContainer>
      </GraphEmbed>
    </div>
  );
};

const LoadingChart = () => {
  return <GraphEmbed isLoading height={100}></GraphEmbed>;
};

export const ChartImpl = ({
  isLoadingQuery,
  data,
  ...props
}: ChartProps & {
  isLoadingQuery: boolean;
  data: AnalyticQuery<any> | undefined;
}) => {
  if (isLoadingQuery || !data) {
    return <LoadingChart />;
  }

  return <Chart data={data} {...props} />;
};
